// Semver / version comparison utilities shared between cluster-manager,
// the manifest pipeline, and the local-api veil-update script generator.
//
// Supports both N-tuple semver ("1.0.10", "147.0.7727.139") and versioned-
// slug keys like "1.0.10-new-headers" — the dash suffix is treated as the
// slug and ignored for ordering.

export function compareSemver(a: string, b: string): number {
	const partsA = semverPartsOf(a);
	const partsB = semverPartsOf(b);
	const len = Math.max(partsA.length, partsB.length);
	for (let i = 0; i < len; i++) {
		const da = partsA[i] ?? 0;
		const db = partsB[i] ?? 0;
		if (da !== db) return da - db;
	}
	return 0;
}

// Strips the "-<slug>" suffix from a versioned-slug key like "1.0.1-new".
export function stripSlug(key: string): string {
	const dash = key.indexOf("-");
	return dash >= 0 ? key.slice(0, dash) : key;
}

// Returns the largest entry in `keys` by semver order, or null if empty.
export function highestSemver(keys: Iterable<string>): string | null {
	let best: string | null = null;
	for (const k of keys) {
		if (best === null || compareSemver(k, best) > 0) best = k;
	}
	return best;
}

// Like `highestSemver`, but only considers keys matching `pattern` (e.g. the
// dotted-quad chromium semver). Useful when a map mixes raw semver and
// versioned-slug keys and we want one shape only.
export function highestSemverKey(
	map: Record<string, unknown>,
	pattern: RegExp,
): string | null {
	const keys = Object.keys(map).filter((k) => pattern.test(k));
	if (keys.length === 0) return null;
	keys.sort(compareSemver);
	return keys[keys.length - 1] ?? null;
}

// Highest of a set of versioned-slug keys ("1.0.10-new-headers"). Slugs are
// ignored when ranking.
export function highestVersionedSlugKey(
	map: Record<string, unknown>,
): string | null {
	const keys = Object.keys(map);
	if (keys.length === 0) return null;
	keys.sort((a, b) => compareSemver(stripSlug(a), stripSlug(b)));
	return keys[keys.length - 1] ?? null;
}

function semverPartsOf(value: string): number[] {
	const bare = stripSlug(value).trim();
	if (!bare) return [];
	return bare.split(".").map((n) => {
		const parsed = Number.parseInt(n, 10);
		return Number.isFinite(parsed) ? parsed : 0;
	});
}
